import { AfterViewInit, Component, HostListener, OnDestroy, ViewChild } from "@angular/core";
import { ComponentCanDeactivate } from "../model/pending-changes.guard";
import { Observable, Subscription } from "rxjs";
import { createLinqsetEdge, EDGE_TYPE, getEdgeType, INSIGHTS_FIELD, Linqset, LinqsetEdge, MODEL_TYPE, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY, TUTORIAL_MODEL_KEY } from "../../common/linqset-model";
import { createInsightsFilter, createOrganisation, ModelDetails, Organisation } from "../../common/organisation";
import { LAYOUT_DIRECTION, ONBOARDING_STEPS } from "../../common/user";
import { v4 } from "uuid";
import { createOnboardingLinqset, END_INDEX, LAST_ROLE_STEP, LAST_SUPPLY_CHAIN_STEP, nodeIndicesForRoleSystem, SUPPLY_CHAIN_INDEX } from "./onboarding-data";
import { NodeService } from "../../model-services/node.service";
import { LabelService } from "../../model-services/label.service";
import { TutorialGraphComponent } from "../../canvas/tutorial-graph/tutorial-graph.component";
import { IMAGE_LINQ, LABEL_TYPES, MODEL_COLOURS } from "../../model-services/model-item-enums";
import { EdgeService } from "../../model-services/edge.service";
import { GraphService } from "../../canvas/graph.service";
import { SCREEN } from "../layouts-enums";
import { FUNCTION, FunctionService } from "../../model-services/function.service";
import { SidenavService } from "../model/sidenav.service";
import { MatSidenav } from "@angular/material/sidenav";
import { processLinqset, createLinqset } from "../../common/model-processing";
import { ActivatedRoute, Router } from "@angular/router";
import { DEFAULT_URL } from "../../app-urls";
import { UserService } from "../../user/user.service";
import { AdjacencyTypes, IEdge, IModelItem, INode, Point } from "@yfiles/yfiles";

const INITIAL_FUNCTIONS = [FUNCTION.ZOOM_IN, FUNCTION.ZOOM_OUT, FUNCTION.FIT_TO_SCREEN, FUNCTION.SWITCH_TO_PAN_MODE, FUNCTION.SWITCH_TO_SELECT_MODE]
const ALL_FUNCTIONS = [FUNCTION.MANAGE_LABELS, FUNCTION.ZOOM_IN, FUNCTION.ZOOM_OUT, FUNCTION.FIT_TO_SCREEN, FUNCTION.RESET_LAYOUT, FUNCTION.SWITCH_TO_PAN_MODE, FUNCTION.SWITCH_TO_SELECT_MODE, FUNCTION.TOGGLE_VIEW_DIRECTION]

// where the walkthrough is: welcoming, building the main flow, tidying its layout, adding the people and systems,
// and done, with the model's insights
export enum WALKTHROUGH_STAGE {
    WELCOME,
    BUILD,
    LAYOUT,
    PEOPLE,
    DONE
}

// the parts of the walkthrough shown in the guide's progress bar
const PARTS = [
    {stage: WALKTHROUGH_STAGE.BUILD, label: 'Main flow'},
    {stage: WALKTHROUGH_STAGE.LAYOUT, label: 'Tidy'},
    {stage: WALKTHROUGH_STAGE.PEOPLE, label: 'People'},
    {stage: WALKTHROUGH_STAGE.DONE, label: 'Insights'},
]

// the steps the user takes: placing five nodes and connecting three of them, tidying the layout, then placing and
// connecting a role, another role and a system. The rest of the model is added for them
const TOTAL_STEPS = 15

// what the guide says at a step: its title, the instruction, and how it's done (drag a node, or draw an edge)
export interface WalkthroughGuide {
    title:string
    html:string
    how?:'drag'|'connect'
}

@Component({
    selector: 'app-onboarding-model-layout',
    templateUrl: './onboarding-model.html',
    styleUrls: ['./onboarding-model.scss'],
    standalone: false
})
export class OnboardingModelComponent implements AfterViewInit, OnDestroy, ComponentCanDeactivate {
    IMAGE_LINQ = IMAGE_LINQ
    screen:SCREEN = SCREEN.ONBOARDING_MODEL
    STAGE = WALKTHROUGH_STAGE
    parts = PARTS
    totalSteps = TOTAL_STEPS

    routeSub:Subscription
    organisationKey:string
    organisation:Organisation
    linqset:Linqset
    organisationLinqset:Linqset

    menuFunctions = INITIAL_FUNCTIONS
    trigger:any = {}

    modelType:MODEL_TYPE = MODEL_TYPE.PROCESS
    stage:WALKTHROUGH_STAGE = WALKTHROUGH_STAGE.WELCOME
    index:number = 0
    endIndex:number = 0
    canSelectNew:boolean

    completed:boolean = false
    insightsTab:number = 0

    FUNCTION = FUNCTION
    MODEL_COLOURS = MODEL_COLOURS

    @ViewChild(TutorialGraphComponent) tutorialGraph: TutorialGraphComponent
    @ViewChild('insightsdrawer') public insightsDrawer: MatSidenav;

    constructor(private nodeService:NodeService, private labelService:LabelService, private router:Router, private route:ActivatedRoute,
        private edgeService:EdgeService, private graphService: GraphService, private functionService:FunctionService, public sidenavService:SidenavService, private userService:UserService) {
        this.organisation = createOrganisation()
        const filter = createInsightsFilter()
        filter.modelKeys = [TUTORIAL_MODEL_KEY]
        this.linqset = createLinqset(this.organisation, filter)

        createOnboardingLinqset(this.linqset, false)

        const nodes = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE || n.superType === NODE_SUPERTYPE.SYSTEM)
        this.organisationLinqset = createLinqset(this.organisation, createInsightsFilter(), {nodes, edges:[]})
        // just need to clean this up because is set when creating linqset
        nodes.forEach(n => n.excluded = false)

        this.organisation.models = [this.linqset.model]
        this.organisation.model = {_key:ORGANISATION_MODEL_KEY, name:"Walkthrough"} as ModelDetails
        this.labelService.nodeInsightsField = INSIGHTS_FIELD.NONE
    }

    ngOnDestroy(): void {
        if (this.routeSub) this.routeSub.unsubscribe()
    }

    ngAfterViewInit(): void {
        if (!this.routeSub) {
            setTimeout(() => {
                this.routeSub = this.route.params.subscribe(async p => {
                    this.organisationKey = p['organisation_key']
                })
            })
        }
        this.tutorialGraph.graphComponent.graph.tag = {orientation: LAYOUT_DIRECTION.LEFT_RIGHT}
    }

    @HostListener('window:beforeunload')
    canDeactivate(): Observable<boolean> | boolean {
        return false
    }

    // the welcome's choices: start, or leave it for now. Either way it counts as seen
    start() {
        this.userService.saveOnboardingStep(ONBOARDING_STEPS.WALKTHROUGH)
        this.stage = WALKTHROUGH_STAGE.BUILD
        this.index = 0
        this.canSelectNew = true
        this.endIndex = SUPPLY_CHAIN_INDEX
    }

    notNow() {
        this.userService.saveOnboardingStep(ONBOARDING_STEPS.WALKTHROUGH)
        this.return()
    }

    async return() {
        this.ngOnDestroy()
        this.router.navigateByUrl(DEFAULT_URL(this.organisationKey))
    }

    // ---------- the guide ----------

    // which step the user is on, counting from 1: placing a node, then connecting it, in each part
    get step():number {
        const connecting = !this.canSelectNew
        switch (this.stage) {
            case WALKTHROUGH_STAGE.BUILD:
                // nodes 0 to 2 are placed, the first two joined for them; then each node is placed and connected
                if (connecting) return 2 * (this.index - 1)
                return this.index <= 2 ? this.index + 1 : 2 * this.index - 1
            case WALKTHROUGH_STAGE.LAYOUT:
                return 9
            case WALKTHROUGH_STAGE.PEOPLE:
                // the roles and the system are nodes 11 to 13
                return connecting ? 2 * (this.index - 11) + 9 : 2 * (this.index - 11) + 10
            default:
                return TOTAL_STEPS
        }
    }

    get partIndex():number {
        return PARTS.findIndex(p => p.stage === this.stage)
    }

    private name(i:number) {
        return `<b>${this.linqset.nodes[i]?.name}</b>`
    }

    // a kind of node, in its colour. Inserted html keeps classes but not styles, so the colours are the guide's own
    private kind(label:string, kind:'action'|'information'|'role'|'system') {
        return `<span class="kind kind-${kind}">${label}</span>`
    }

    get guide():WalkthroughGuide {
        const connecting = !this.canSelectNew
        const action = this.kind('Actions', 'action')
        const information = this.kind('Information', 'information')

        switch (this.stage) {
            case WALKTHROUGH_STAGE.BUILD: {
                if (connecting) {
                    // the two nodes placed last, the earlier one first
                    return {title: 'Connect them with an edge', how: 'connect',
                        html: `<p>Point at the middle of ${this.name(this.index - 2)} until a blue dot appears, then drag to ${this.name(this.index - 1)} and let go.</p><p>The edge shows the order of the flow: what each step uses and creates.</p>`}
                }
                switch (this.index) {
                    case 0: return {title: 'Add the first action', how: 'drag',
                        html: `<p>${action} are the work that gets done. A process starts with an action that brings something in, here a job brief.</p><p>Drag ${this.name(0)} from the strip above onto the canvas.</p>`}
                    case 1: return {title: 'Add what it brings in', how: 'drag',
                        html: `<p>${information} is what an action creates or changes, and passes on.</p><p>Drag ${this.name(1)} onto the canvas, below ${this.name(0)}. LINQ connects these two for you.</p>`}
                    case 2: return {title: 'Add the next action', how: 'drag',
                        html: `<p>The job brief is used to write the ad.</p><p>Drag ${this.name(2)} onto the canvas, beside ${this.name(1)}.</p>`}
                    case 3: return {title: 'Add what it creates', how: 'drag',
                        html: `<p>Drag ${this.name(3)} onto the canvas, beside ${this.name(2)}.</p>`}
                    default: return {title: 'Add the approval', how: 'drag',
                        html: `<p>Drag ${this.name(4)} onto the canvas, beside ${this.name(3)}.</p>`}
                }
            }
            case WALKTHROUGH_STAGE.LAYOUT:
                return {title: 'Tidy the layout',
                    html: `<p>That's the pattern: actions and the information they pass on. LINQ has added the rest of the main flow for you: posting the ad to the internal board and a job website.</p><p>It's a little untidy. Choose <b>Tidy the layout</b> below, or <b>Reset layout</b> (highlighted) on the toolbar, and LINQ lays it out.</p>`}
            case WALKTHROUGH_STAGE.PEOPLE: {
                const roles = this.kind('Roles', 'role')
                const systems = this.kind('Systems', 'system')
                const placed = this.index - 1
                if (connecting) {
                    const isRole = this.linqset.nodes[placed]?.superType === NODE_SUPERTYPE.ROLE
                    return {title: isRole ? 'Connect it to the actions it does' : 'Connect it to what uses it', how: 'connect',
                        html: isRole
                            ? `<p>Drag an edge from ${this.name(placed)} to each highlighted action. LINQ draws a copy of the role beside each one, to keep the model readable.</p>`
                            : `<p>Drag an edge from ${this.name(placed)} to each highlighted node: the roles that use it, and the information kept in it.</p>`}
                }
                switch (this.index) {
                    case 11: return {title: 'Add who does the work', how: 'drag',
                        html: `<p>${roles} are the people who do the work.</p><p>Drag ${this.name(11)} onto the canvas, above the flow.</p>`}
                    case 12: return {title: 'Add the approver', how: 'drag',
                        html: `<p>Drag ${this.name(12)} onto the canvas, above ${this.name(4)}.</p>`}
                    default: return {title: 'Add a system', how: 'drag',
                        html: `<p>${systems} are the tools used to do the work, and where information is kept.</p><p>Drag ${this.name(13)} onto the canvas, above the flow.</p>`}
                }
            }
            default:
                return {title: 'Your model is complete',
                    html: `<p>LINQ connected the remaining systems for you and worked out the time and cost of the whole process. The number on each node is its cost a year.</p><p>The panel on the right shows the model's insights. That's how LINQ shows where the time and money in a process goes.</p>`}
        }
    }

    // ---------- the toolbar and the canvas ----------

    restart() {
        this.tutorialGraph.graphComponent.graph.clear()
        this.insightsDrawer.close()
        this.completed = false
        this.insightsTab = 0

        // the model starts again with nothing connected, as it did at first
        this.linqset.edges = []
        this.organisationLinqset.edges = []
        this.labelService.nodeInsightsField = INSIGHTS_FIELD.NONE
        this.tutorialGraph.graphComponent.graph.tag = {orientation: LAYOUT_DIRECTION.LEFT_RIGHT}
        this.menuFunctions = INITIAL_FUNCTIONS

        this.stage = WALKTHROUGH_STAGE.BUILD
        this.index = 0
        this.canSelectNew = true
        this.endIndex = SUPPLY_CHAIN_INDEX
    }

    // the guide's way of tidying, the same as the toolbar's Reset layout
    tidyLayout() {
        this.handleFunctionEvent({function: FUNCTION.RESET_LAYOUT})
    }

    seeInsights() {
        this.insightsTab = 1
        this.insightsDrawer.open()
    }

    async handleFunctionEvent(event:{function:FUNCTION, value?:any}) {
        const {graphComponent} = this.tutorialGraph

        if (event.function === FUNCTION.TOGGLE_VIEW_DIRECTION) {
            this.labelService.removeLabels(graphComponent.graph, true)
            graphComponent.graph.tag = {orientation: event.value as LAYOUT_DIRECTION}
            this.labelService.updateLabelsForGraph(graphComponent.graph, false, this.linqset)
        }

        if (event.function === FUNCTION.RESET_LAYOUT || event.function === FUNCTION.TOGGLE_VIEW_DIRECTION) {
            await this.graphService.runLayoutForLinqset(graphComponent, this.linqset)

            // tidying the finished main flow moves on to the people and systems
            if (this.stage === WALKTHROUGH_STAGE.LAYOUT) {
                this.endIndex = END_INDEX
                this.stage = WALKTHROUGH_STAGE.PEOPLE
            }
        }else await this.functionService.executeFunction(event.function, event.value, this.linqset, this.linqset.project, graphComponent)

        const start = graphComponent.viewPoint
        graphComponent.graph.nodes.filter(n => n.tag.index >= this.index).forEach(n => {
            graphComponent.graph.setNodeCenter(n, new Point(start.x+(150*( n.tag.index-this.index+1)), start.y+150));
        })
    }

    // what the user is to connect next is highlighted; nothing else is
    clearNodes() {
        const {graphComponent} = this.tutorialGraph
        graphComponent.highlightIndicatorManager.items.clear()
        graphComponent.selection.clear()
    }

    // node has been dragged so process and move to next index
    nodeCreated(node:INode) {
        if (this.index >= END_INDEX) return

        const {graphComponent} = this.tutorialGraph
        const {graph} = graphComponent
        if (this.index > LAST_SUPPLY_CHAIN_STEP && node.tag.node.name !== this.linqset.nodes[this.index].name) return     // duplicate role or system

        graphComponent.highlightIndicatorManager.items.clear()
        graphComponent.selection.clear()
        node.tag.index = this.index

        this.index++

        if (this.index <= 1) return

        if (this.index === 2) {
            // after they have moved the first two nodes onto canvas join them together
            const secondNode:INode = graph.nodes.last()
            const startingNode:INode = graph.nodes.first()

            this.edgeService.createNewEdge(graph, this.linqset, startingNode, secondNode)
        }else if (this.index <= LAST_SUPPLY_CHAIN_STEP) {
            // supply chain steps: the node placed before this one is highlighted, to draw the edge from
            this.canSelectNew = false
            const previous = graph.nodes.at(this.index-2)
            graphComponent.highlightIndicatorManager.items.add(previous)
            previous.tag.disabledEdge = false

            const last = graph.nodes.last()
            last.tag.disabledEdge = false
        }else {
            // role and systems steps: the nodes it connects to are highlighted
            this.clearNodes()
            this.canSelectNew = false
            const last:INode = graph.nodes.last()
            last.tag.disabledEdge = false

            const indices = nodeIndicesForRoleSystem(last.tag.index)
            graph.nodes.filter(n => indices.indexOf(n.tag.index) >= 0).forEach(n => {
                if (n.tag.node.superType === NODE_SUPERTYPE.INFORMATION || last.tag.node.superType === NODE_SUPERTYPE.ROLE) {
                    graphComponent.highlightIndicatorManager.items.add(n)
                    n.tag.disabledEdge = false
                }else {
                    // connecting system to instance of role but have action
                    const e = graph.edgesAt(n, AdjacencyTypes.INCOMING).find(e => e.sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE)
                    if (e) {
                        graphComponent.highlightIndicatorManager.items.add(e.sourceNode)
                        e.sourceNode.tag.disabledEdge = false
                    }
                }
            })
        }
    }

    async handleItemCreated(item:IModelItem) {
        if (item instanceof IEdge) {
            await this.edgeCreated(item)
        }else if (item instanceof INode) {
            this.nodeCreated(item)
        }
    }

    async edgeCreated(edge:IEdge) {
        if (this.index >= END_INDEX) return

        const {graphComponent} = this.tutorialGraph

        this.addEdgeToLinqset(edge)

        if (this.index <= LAST_SUPPLY_CHAIN_STEP) {
            this.clearNodes()
            this.canSelectNew = true
        }
        if (this.index === LAST_SUPPLY_CHAIN_STEP) {
            // end of supply chain so complete it and start roles and systems
            edge.sourceNode.tag.disabledEdge = true
            edge.targetNode.tag.disabledEdge = true

            await this.completeSupplyChain()
        }else if (this.index < LAST_SUPPLY_CHAIN_STEP) {
            edge.sourceNode.tag.disabledEdge = true
            edge.targetNode.tag.disabledEdge = true

        }else {
            // roles and systems
            graphComponent.selection.clear()
            edge.targetNode.tag.disabledEdge = true

            if (graphComponent.highlightIndicatorManager.items.size <= 1) {
                this.clearNodes()
                this.canSelectNew = true
                edge.sourceNode.tag.disabledEdge = true
                if (this.index >= LAST_ROLE_STEP) {
                    // complete the model
                    this.completeModel()
                }
            }
        }
    }

    // the rest of the main flow is added for the user, then they tidy it
    async completeSupplyChain() {
        const {graph} = this.tutorialGraph.graphComponent
        const n = graph.nodes.last()
        const y = n.layout.y + 200

        for (let i = this.index; i<=SUPPLY_CHAIN_INDEX; i++) {
            const node = this.nodeService.createNodeAt(this.linqset, graph, this.linqset.nodes[i], v4(), new Point(graph.nodes.at(i-1).layout.x + 200, y))
            this.labelService.addLabelToNode(graph, node, this.linqset.nodes[i].name, LABEL_TYPES.NODE_NAME)
            if (i < 8) this.createEdge(i-1, i)
        }

        this.createEdge(5, 8)
        this.createEdge(8, 9)

        this.createEdge(7, 10)
        this.createEdge(9, 10)

        this.index = SUPPLY_CHAIN_INDEX+1
        this.menuFunctions = ALL_FUNCTIONS
        this.stage = WALKTHROUGH_STAGE.LAYOUT
    }

    addEdgeToLinqset(edge:IEdge) {
        const edgeData = {type:getEdgeType(edge.sourceNode.tag.node, edge.targetNode.tag.node), modelKey: this.linqset.modelKey, sourceKey: edge.sourceNode.tag.node._key, targetKey:edge.targetNode.tag.node._key, sourceVisualisationKey:edge.sourceNode.tag.visualisationKey, targetVisualisationKey:edge.targetNode.tag.visualisationKey} as LinqsetEdge
        const linqsetEdge = createLinqsetEdge(v4(), this.linqset.organisationKey, this.linqset.workspaceKey, edgeData)
        this.linqset.edges.push(linqsetEdge)
        if (edgeData.type === EDGE_TYPE.USEBY) this.organisationLinqset.edges.push(linqsetEdge)
    }

    addNodeToGraph(i:number) {
        const {graph} = this.tutorialGraph.graphComponent

        const node = this.nodeService.createNode(this.linqset, this.linqset.nodes[i], v4(), graph)
        this.labelService.addLabelToNode(graph, node, this.linqset.nodes[i].name, LABEL_TYPES.NODE_NAME)
        return node
    }

    async completeModel() {
        const {graph} = this.tutorialGraph.graphComponent
        this.clearNodes()
        this.index = END_INDEX

        for (let i=LAST_ROLE_STEP; i< this.linqset.nodes.length; i++) {
            if (i === 14) {
                // MS Word
                const n1 =this.addNodeToGraph(i)
                this.createEdgeBetweenNodes(n1, graph.nodes.at(3))
                const role =this.getRoleForAction(2)
                if (role) {
                    const n2 =this.addNodeToGraph(i)
                    this.createEdgeBetweenNodes(n2, role)
                }
            }else if (i === 15) {
                // Intranet
                const n1 =this.addNodeToGraph(i)
                this.createEdgeBetweenNodes(n1, graph.nodes.at(7))
                const role =this.getRoleForAction(6)
                if (role) {
                    const n2 =this.addNodeToGraph(i)
                    this.createEdgeBetweenNodes(n2, role)
                }

            }else if (i === 16) {
                // Job website
                const n1 =this.addNodeToGraph(i)
                this.createEdgeBetweenNodes(n1, graph.nodes.at(9))
                const role =this.getRoleForAction(8)
                if (role) {
                    const n2 =this.addNodeToGraph(i)
                    this.createEdgeBetweenNodes(n2, role)
                }
            }
        }

        this.stage = WALKTHROUGH_STAGE.DONE
        await this.insightsDrawer.open()
        await processLinqset(this.linqset, this.organisationLinqset, true)
        this.labelService.nodeInsightsField = INSIGHTS_FIELD.COST

        this.tutorialGraph.graphComponent.graph.tag = {orientation:LAYOUT_DIRECTION.DOWN}
        this.labelService.updateLabelsForGraph(this.tutorialGraph.graphComponent.graph, false, this.linqset, INSIGHTS_FIELD.COST)
        await this.graphService.runLayoutForLinqset(this.tutorialGraph.graphComponent, this.linqset)
        this.completed = true
        this.trigger = {}
    }

    createEdge(source:number, target:number) {
        const {graph} = this.tutorialGraph.graphComponent

        const sourceNode = graph.nodes.at(source)
        const targetNode = graph.nodes.at(target)
        return this.createEdgeBetweenNodes(sourceNode, targetNode)
    }

    createEdgeBetweenNodes(sourceNode:INode, targetNode:INode) {
        const {graphComponent} = this.tutorialGraph

        const edge = this.edgeService.createNewEdge(graphComponent.graph, this.linqset, sourceNode, targetNode)
        this.addEdgeToLinqset(edge)
        return edge
    }

    getRoleForAction(actionIndex:number) {
        const {graph} = this.tutorialGraph.graphComponent
        const action = graph.nodes.at(actionIndex)
        const edges = graph.edgesAt(action, AdjacencyTypes.INCOMING).filter(e => e.sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE)
        if (edges.size > 0) return edges.at(0).sourceNode
        return undefined
    }
}
